import { z } from 'zod';

export const MICROSOFT_SERVICES = [
  'onedrive',
  'outlook',
  'onenote',
  'teams',
] as const;
export type MicrosoftService = (typeof MICROSOFT_SERVICES)[number];

const microsoftServiceSchema = z.enum(MICROSOFT_SERVICES);

export const microsoftConnectBodySchema = z.object({
  accessToken: z.string(),
  extra: z.object({
    tenantId: z.string(),
  }),
  services: z.array(microsoftServiceSchema).optional(),
});

const microsoftConnectResponseSchema = z.object({
  connected: z.boolean(),
  services: z.array(microsoftServiceSchema),
});

export type MicrosoftConnectBody = {
  accessToken: string;
  extra: {
    tenantId: string;
  };
  services?: MicrosoftService[];
};
export type MicrosoftConnectResponse = z.infer<
  typeof microsoftConnectResponseSchema
>;

const integrationServiceSchema = z.object({
  type: z.union([microsoftServiceSchema, z.string()]),
  connected: z.boolean(),
});

const integrationStatusSchema = z.object({
  provider: z.string(),
  services: z.array(integrationServiceSchema),
});

const integrationsStatusResponseSchema = z.array(integrationStatusSchema);

export type IntegrationService = z.infer<typeof integrationServiceSchema>;
export type IntegrationStatus = z.infer<typeof integrationStatusSchema>;
export type IntegrationsStatusResponse = z.infer<
  typeof integrationsStatusResponseSchema
>;

const microsoftConnectedConvoSchema = z.object({
  id: z.string(),
  fileCount: z.number(),
});

const microsoftConnectedConvosResponseSchema = z.object({
  documentCount: z.number(),
  conversationCount: z.number(),
  conversations: z.array(microsoftConnectedConvoSchema),
});

export type MicrosoftConnectedConvo = z.infer<
  typeof microsoftConnectedConvoSchema
>;
export type MicrosoftConnectedConvosResponse = z.infer<
  typeof microsoftConnectedConvosResponseSchema
>;

const microsoftItemResponseSchema = z.object({
  url: z.string(),
});

export type MicrosoftItemResponse = z.infer<typeof microsoftItemResponseSchema>;

export interface MicrosoftItemLinkParams {
  service: MicrosoftService;
  itemId: string;
  conversationId: string;
}

const apiErrorSchema = z.object({
  code: z.string(),
  message: z.string(),
});

export type ApiError = z.infer<typeof apiErrorSchema>;

const notionConnectResponseSchema = z.object({
  connected: z.boolean(),
  services: z.array(z.string()),
});

export type NotionConnectBody = {
  extra: {
    code: string;
    codeVerifier: string;
    redirectUri: string;
  };
};
export type NotionConnectResponse = z.infer<typeof notionConnectResponseSchema>;

const connectorStatusSchema = z.enum(['connected', 'disconnected']);
const connectorKindSchema = z.enum(['managed', 'custom']);
const connectorAuthTypeSchema = z.union([
  z.enum(['apiKey', 'oauth', 'none', 'json', 'catalog']),
  z.string(),
]);

const connectorServiceSchema = z.object({
  serviceId: z.string(),
  status: connectorStatusSchema,
});

const connectorSchema = z.object({
  providerId: z.string(),
  name: z.string(),
  kind: connectorKindSchema,
  status: connectorStatusSchema,
  authType: connectorAuthTypeSchema.optional(),
  serverUrl: z.string().optional(),
  services: z.array(connectorServiceSchema).optional(),
});

const connectorsResponseSchema = z.object({
  connectors: z.array(connectorSchema),
});

export type ConnectorStatus = z.infer<typeof connectorStatusSchema>;
export type ConnectorKind = z.infer<typeof connectorKindSchema>;
export type ConnectorAuthType = z.infer<typeof connectorAuthTypeSchema>;
export type ConnectorService = z.infer<typeof connectorServiceSchema>;
export type Connector = z.infer<typeof connectorSchema>;
export type ConnectorsResponse = z.infer<typeof connectorsResponseSchema>;

export const CONNECTOR_AUTH_MODES = ['oauth', 'apiKey', 'json'] as const;
export type ConnectorAuthMode = (typeof CONNECTOR_AUTH_MODES)[number];

export type ConnectorConnectBody = {
  /** Reconnects an existing connector; omit to mint a new one. */
  providerId?: string;
  name?: string;
  authType: ConnectorAuthMode;
  serverUrl?: string;
  apiKey?: string;
  /** The pasted `mcpServers` block, used when authType is `json`. */
  config?: string;
};

const connectorConnectResponseSchema = z.object({
  status: z.enum(['connected', 'failed']),
  connector: connectorSchema.optional(),
  error: apiErrorSchema.optional(),
});

export type ConnectorConnectResponse = z.infer<
  typeof connectorConnectResponseSchema
>;

export type RotateConnectorKeyBody = {
  apiKey: string;
};

const rotateConnectorKeyResponseSchema = z.object({
  status: z.enum(['rotated', 'failed']),
  error: apiErrorSchema.optional(),
});

export type RotateConnectorKeyResponse = z.infer<
  typeof rotateConnectorKeyResponseSchema
>;

const connectorToolSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  enabled: z.boolean(),
});

const connectorToolsResponseSchema = z.object({
  providerId: z.string().optional(),
  toolCount: z.number(),
  estimatedTokens: z.number(),
  limit: z.object({
    maxTools: z.number(),
    maxSchemaBytes: z.number(),
  }),
  exceedsLimit: z.boolean(),
  status: z.enum(['ok', 'unavailable']),
  tools: z.array(connectorToolSchema),
});

export type ConnectorTool = z.infer<typeof connectorToolSchema>;
export type ConnectorToolsResponse = z.infer<
  typeof connectorToolsResponseSchema
>;

export const alfredIntegrations = {
  microsoftConnect: {
    path: 'alfred-integrations/v1/integrations/microsoft/connect',
    schema: microsoftConnectResponseSchema,
  },
  microsoftDisconnect: {
    path: 'alfred-integrations/v1/integrations/microsoft/disconnect',
  },
  notionConnect: {
    path: 'alfred-integrations/v1/integrations/notion/connect',
    schema: notionConnectResponseSchema,
  },
  notionDisconnect: {
    path: 'alfred-integrations/v1/integrations/notion/disconnect',
  },
  integrationsStatus: {
    path: 'alfred-integrations/v1/integrations/status',
    schema: integrationsStatusResponseSchema,
  },
  connectedConversations: {
    path: (provider: string, service: string) =>
      `alfred-integrations/v1/integrations/${provider}/services/${service}/connected-convos`,
    schema: microsoftConnectedConvosResponseSchema,
  },
  microsoftItemLink: {
    path: ({ service, itemId, conversationId }: MicrosoftItemLinkParams) => {
      const params = new URLSearchParams({ conversation_id: conversationId });
      return `alfred-integrations/v1/integrations/microsoft/services/${service}/items/${itemId}?${params}`;
    },
    schema: microsoftItemResponseSchema,
  },
  connectors: {
    path: 'alfred-integrations/v2/integrations/status',
    schema: connectorsResponseSchema,
  },
  connectorConnect: {
    path: 'alfred-integrations/v2/integrations',
    schema: connectorConnectResponseSchema,
  },
  connectorDisconnect: {
    path: (connectorId: string) =>
      `alfred-integrations/v2/integrations/${connectorId}`,
  },
  connectorRotateKey: {
    path: (connectorId: string) =>
      `alfred-integrations/v2/integrations/${connectorId}/rotate-key`,
    schema: rotateConnectorKeyResponseSchema,
  },
  connectorTools: {
    path: (connectorId: string) =>
      `alfred-integrations/v2/integrations/${connectorId}/tools`,
    schema: connectorToolsResponseSchema,
  },
} as const;
